import React, { useEffect, useRef, useState } from "react";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { ScanLine } from "lucide-react";
import { looksCompleteScan } from "../lib/barcode";

export default function BarcodeScanner({
  onScan,
  placeholder = "Código de barras / protocolo",
  autoFocus = false,
  disabled = false,
  inputClassName = "",
}) {
  const [value, setValue] = useState("");
  const [scanning, setScanning] = useState(false);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const inputRef = useRef(null);
  const scanningRef = useRef(false);
  const lastRef = useRef({ code: "", at: 0 });
  const onScanRef = useRef(onScan);

  useEffect(() => {
    onScanRef.current = onScan;
  }, [onScan]);

  useEffect(() => () => {
    streamRef.current?.getTracks?.().forEach((t) => t.stop());
  }, []);

  useEffect(() => {
    if (autoFocus && !disabled) inputRef.current?.focus();
  }, [autoFocus, disabled]);

  const fire = (code) => {
    const v = (code || value).trim();
    if (!v || disabled) return;
    const now = Date.now();
    if (lastRef.current.code === v && now - lastRef.current.at < 500) return;
    lastRef.current = { code: v, at: now };
    setValue("");
    onScanRef.current?.(v);
    requestAnimationFrame(() => inputRef.current?.focus());
  };

  useEffect(() => {
    if (!looksCompleteScan(value)) return undefined;
    const timer = setTimeout(() => fire(value), 220);
    return () => clearTimeout(timer);
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  const startCamera = async () => {
    if (!navigator.mediaDevices?.getUserMedia) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment" },
      });
      streamRef.current = stream;
      if (videoRef.current) videoRef.current.srcObject = stream;
      scanningRef.current = true;
      setScanning(true);
      if ("BarcodeDetector" in window) {
        const detector = new window.BarcodeDetector({ formats: ["code_128", "ean_13", "qr_code", "code_39"] });
        const tick = async () => {
          if (!videoRef.current || !scanningRef.current) return;
          try {
            const codes = await detector.detect(videoRef.current);
            if (codes[0]?.rawValue) {
              fire(codes[0].rawValue);
              scanningRef.current = false;
              setScanning(false);
              stream.getTracks().forEach((t) => t.stop());
              return;
            }
          } catch { /* ignore */ }
          if (scanningRef.current) requestAnimationFrame(tick);
        };
        videoRef.current.onloadedmetadata = () => tick();
      }
    } catch {
      scanningRef.current = false;
      setScanning(false);
    }
  };

  return (
    <div className="space-y-2" data-testid="barcode-scanner">
      <div className="flex gap-2">
        <Input
          ref={inputRef}
          value={value}
          disabled={disabled}
          autoComplete="off"
          autoCorrect="off"
          spellCheck={false}
          className={inputClassName}
          onChange={(e) => setValue(e.target.value)}
          placeholder={placeholder}
          onKeyDown={(e) => e.key === "Enter" && fire()}
          data-testid="barcode-input"
        />
        <Button type="button" variant="outline" disabled={disabled} onClick={() => fire()} data-testid="barcode-submit">
          OK
        </Button>
        <Button type="button" variant="outline" disabled={disabled} onClick={startCamera} data-testid="barcode-camera">
          <ScanLine size={16} />
        </Button>
      </div>
      {scanning && (
        <video ref={videoRef} autoPlay playsInline muted className="w-full max-h-40 rounded-xl bg-black object-cover" />
      )}
    </div>
  );
}
